import {
  SupplierTripListItem,
  supplierTripListQuery,
} from "@/app/SupplierTrips/GraphQL/supplierTripListQuery";
import {
  HireUnitType,
  QuerySupplierTripsWhereColumn,
  RelocationInclusionType,
} from "@/gql/graphql";
import { useMemo } from "react";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SupplierTripCreateDialog } from "@/app/SupplierTrips/Components/SupplierTripCreateDialog";
import { ShipIcon, FullscreenIcon } from "lucide-react";
import { SupplierTripEditDialog } from "@/app/SupplierTrips/Components/SupplierTripEditDialog";
import {
  useFetchData,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { formatDistance } from "@/lib/Formatters/formatCurrency";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { Button } from "@/components/catalyst/button";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  supplierOfficeListQuery,
  supplierOfficeListVariables,
} from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { PlusIcon } from "@heroicons/react/20/solid";

const hireUnitDisplayMap = {
  [HireUnitType.Day]: "days",
  [HireUnitType.Night]: "nights",
  [HireUnitType.TwentyFourHours]: "24hrs",
};

export function SupplierTripMatrix({
  supplierId,
  showExpandButton,
  onExpand,
}: {
  supplierId: string;
  showExpandButton?: boolean;
  onExpand?: () => void;
}) {
  const { data: officeData } = useSuspenseGqlQuery(
    supplierOfficeListQuery,
    supplierOfficeListVariables(supplierId),
  );

  const supplierOffices = officeData.supplierOffices.data;

  const fetcher = useFetchData(supplierTripListQuery);
  const { data: supplierTrips } = useSuspenseQuery({
    queryFn: async () => {
      let page = 0;
      const items = [];

      while (true) {
        const res = await fetcher({
          first: 200,
          page,
          where: {
            column: QuerySupplierTripsWhereColumn.SupplierId,
            value: supplierId,
          },
        });

        items.push(...res.supplierTrips.data);
        page++;

        if (!res.supplierTrips.paginatorInfo.hasMorePages) {
          break;
        }
      }

      return items;
    },
    queryKey: [
      "AllTrips",
      {
        supplierId,
      },
    ],
  });

  const matrixData = useMemo(() => {
    const officeIds = new Set(supplierOffices.map((office) => office.id));
    const validTrips = supplierTrips.filter(
      (trip) =>
        officeIds.has(trip.officeA.id) && officeIds.has(trip.officeB.id),
    );

    return supplierOffices.map((officeA) => {
      return supplierOffices.map((officeB) => {
        return validTrips.find((trip) => {
          return (
            (trip.officeB.id === officeB.id &&
              trip.officeA.id === officeA.id) ||
            (trip.officeB.id === officeA.id && trip.officeA.id === officeB.id)
          );
        });
      });
    });
  }, [supplierTrips, supplierOffices]);

  const numCols = matrixData.at(0)?.length ?? 0;
  const numRows = matrixData.length;

  const { open: openNew } = useDialog(SupplierTripCreateDialog);
  const { open: openEdit } = useDialog(SupplierTripEditDialog);

  if (supplierTrips.length === 0) {
    return (
      <RecordScreenCard
        buttons={
          <Button
            onClick={() => {
              openNew({
                supplierId,
              });
            }}
          >
            Add trip
          </Button>
        }
      >
        No trips found
      </RecordScreenCard>
    );
  }

  return (
    <div
      className="isolate grid max-h-[80vh] w-full overflow-auto rounded-lg border border-gray-300 shadow-sm"
      style={{
        gridTemplateColumns: `10rem repeat(${numCols}, minmax(6rem, 1fr))`,
        gridTemplateRows: `3rem repeat(${numRows}, 3rem)`,
      }}
    >
      <div className="sticky left-0 top-0 z-50 border-b border-r border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100">
        {showExpandButton ? (
          <button
            onClick={onExpand}
            className="flex h-full w-full items-center justify-center transition hover:bg-gray-200/50"
            title="Expand matrix"
          >
            <FullscreenIcon className="size-4 text-gray-500" />
          </button>
        ) : null}
      </div>

      {/* Column headings */}
      {supplierOffices.map((office, index) => (
        <div
          key={`col-${index}`}
          className="sticky top-0 z-30 flex items-center border-b border-gray-300 bg-gradient-to-b from-gray-50 to-gray-100 px-3 py-2 text-xs font-semibold text-gray-700"
          style={{
            borderRight:
              index < numCols - 1 ? "1px solid rgb(209 213 219)" : undefined,
          }}
        >
          {office?.name || "Unknown Office"}
        </div>
      ))}

      {matrixData.map((tripRow, rowIndex) => {
        const rowOffice = supplierOffices[rowIndex];
        return (
          <div key={rowOffice?.id ?? `row-${rowIndex}`} className="contents">
            {/* Row headings */}
            <div
              className="sticky left-0 z-20 flex items-center border-r border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100 px-3 py-2 text-xs font-semibold text-gray-700"
              style={{
                borderBottom:
                  rowIndex < numRows - 1
                    ? "1px solid rgb(209 213 219)"
                    : undefined,
              }}
            >
              {rowOffice?.name || "Unknown Office"}
            </div>
            {tripRow.map((trip, colIndex) => (
              <SupplierTripCell
                key={`cell-${rowIndex}-${colIndex}`}
                trip={trip}
                rowIndex={rowIndex}
                colIndex={colIndex}
                numRows={numRows}
                numCols={numCols}
                onClick={() => {
                  if (trip) {
                    openEdit({
                      officeAId: trip.officeA.id,
                      officeBId: trip.officeB.id,
                    });
                  } else {
                    const colOffice = supplierOffices[colIndex];

                    if (rowOffice && colOffice) {
                      openNew({
                        supplierId,
                        initialValues: {
                          officeA: {
                            connect: rowOffice.id,
                          },
                          officeB: {
                            connect: colOffice.id,
                          },
                        },
                      });
                    }
                  }
                }}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function SupplierTripCell({
  trip,
  rowIndex,
  colIndex,
  numRows,
  numCols,
  onClick,
}: {
  trip: SupplierTripListItem | undefined;
  rowIndex: number;
  colIndex: number;
  numRows: number;
  numCols: number;
  onClick: () => void;
}) {
  const isLastRow = rowIndex === numRows - 1;
  const isLastCol = colIndex === numCols - 1;

  if (!trip) {
    return (
      <div
        className="group flex h-full w-full items-center justify-center bg-white transition hover:bg-blue-50"
        style={{
          borderRight: !isLastCol ? "1px solid rgb(229 231 235)" : undefined,
          borderBottom: !isLastRow ? "1px solid rgb(229 231 235)" : undefined,
        }}
      >
        <Button
          onClick={onClick}
          color="indigo"
          className="truncate opacity-0 transition group-hover:opacity-100"
        >
          <PlusIcon /> Add
        </Button>
      </div>
    );
  }

  return (
    <button
      className="focus:outline-hidden relative flex h-full w-full cursor-pointer flex-col justify-center bg-white text-sm transition hover:bg-blue-50"
      style={{
        borderRight: !isLastCol ? "1px solid rgb(229 231 235)" : undefined,
        borderBottom: !isLastRow ? "1px solid rgb(229 231 235)" : undefined,
      }}
      onClick={onClick}
    >
      {trip?.inclusions.find(
        (inclusion) => inclusion.type === RelocationInclusionType.Ferry,
      ) ? (
        <div className="absolute right-2 top-1.5 flex items-center justify-center">
          <ShipIcon className="size-4 text-blue-600" />
        </div>
      ) : null}
      <div className="flex flex-col px-3">
        <span className="font-semibold text-gray-900">
          {trip?.hire_units_allowed}{" "}
          <span className="text-xs font-normal text-gray-500">
            {hireUnitDisplayMap[trip.supplier.hire_unit_type]}
          </span>
        </span>
        <span className="text-xs text-gray-600">
          {formatDistance(trip.distance_allowed, trip.supplier.measurement)}
        </span>
      </div>
    </button>
  );
}
